import {
  DispatchTaskAssignmentView,
  ResultStatus,
  TaskAssignmentOrEvent,
  TaskEvent,
} from '@dispatch/shared/types';
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
} from '@mui/lab';
import { Stack, SvgIconTypeMap, Typography } from '@mui/material';
import {
  Assignment,
  DirectionsWalk,
  DirectionsRun,
  PersonAdd,
  PersonRemove,
  EventRepeatSharp,
  LockOpenSharp,
  Loop,
  CheckCircleSharp,
  CancelSharp,
  QuestionMark,
  LockSharp,
  AssignmentLate,
  CheckSharp,
  ErrorOutlineSharp,
} from '@mui/icons-material';
import { Loading, Section } from '@dispatch/web/ui';
import { OverridableComponent } from '@mui/material/OverridableComponent';

export type TaskTimelineTabProps = {
  data?: TaskAssignmentOrEvent[];
  status?: ResultStatus;
};

const timeLineLookup: Record<
  string,
  {
    icon: OverridableComponent<SvgIconTypeMap<{}, 'svg'>>;
    title: string;
    showCreatedBy?: boolean;
  }
> = {
  'assignment-created': { icon: Assignment, title: 'Task Added To The System' },
  'adjuster-arrived': { icon: DirectionsWalk, title: 'Adjuster has arrived' },
  'adjuster-departed': { icon: DirectionsRun, title: 'Adjuster has departed' },
  'adjuster-assigned': { icon: PersonAdd, title: 'Adjuster Assigned' },
  'adjuster-reassigned': { icon: PersonAdd, title: 'Adjuster Reassigned' },
  'adjuster-removed': {
    icon: PersonRemove,
    title: 'Adjuster Removed',
    showCreatedBy: true,
  },
  'assignment-rescheduled': {
    icon: EventRepeatSharp,
    title: 'Assignment Rescheduled',
  },
  'assignment-locked': {
    icon: LockSharp,
    title: 'Assignment Locked Sent',
    showCreatedBy: true,
  },
  'assignment-unlocked': {
    icon: LockOpenSharp,
    title: 'Assignment Unlocked',
    showCreatedBy: true,
  },
  'adjuster-started-inspection': { icon: Loop, title: 'Inspection Started' },
  'adjuster-completed-inspection': {
    icon: CheckCircleSharp,
    title: 'Inspection Completed',
  },
  'assignment-cancelled': {
    icon: CancelSharp,
    title: 'Task Canceled',
    showCreatedBy: true,
  },
  'assignment-optimized': {
    icon: CancelSharp,
    title: 'Task Optimized',
    showCreatedBy: true,
  },
  'assignment-sent-to-naq': {
    icon: ErrorOutlineSharp,
    title: 'Task Sent To NAQ',
  },
  OPTIMIZED: {
    icon: CheckSharp,
    title: 'Task Optimized',
    showCreatedBy: true,
  },
  UI_REASSIGN: {
    icon: PersonAdd,
    title: 'Task Reassigned',
    showCreatedBy: true,
  },
  FORCED: {
    icon: AssignmentLate,
    title: 'Assignment Forced',
    showCreatedBy: true,
  },
  initial_assignment: {
    icon: Assignment,
    title: 'Initial',
    showCreatedBy: true,
  },
  initial_forced_assignment: {
    icon: Assignment,
    title: 'Initial Forced',
    showCreatedBy: true,
  },
};

const RenderTimelineItem = ({
  item,
  isLast,
}: {
  item: TaskAssignmentOrEvent;
  isLast: boolean;
}) => {
  // Type guard to check if the item is a DispatchTaskAssignmentView
  const isDispatchTaskAssignmentView = (
    obj: TaskAssignmentOrEvent
  ): obj is DispatchTaskAssignmentView => {
    return 'assignmentAction' in obj;
  };

  // Type guard to check if the item is a TaskEvent
  const isTaskEvent = (obj: TaskAssignmentOrEvent): obj is TaskEvent => {
    return 'eventDateTime' in obj;
  };

  // Extract eventCode, eventDateTime, and createdBy based on the type
  let eventCode: string | undefined;
  let eventDateTime: string | undefined;
  let createdBy: string | undefined;
  let adjusterName: string | undefined;

  if (isDispatchTaskAssignmentView(item)) {
    if (String(item.assignmentAction?.actionType) === 'INITIAL') {
      eventCode = String(item.assignmentAction?.reason?.code);
    } else {
      eventCode = String(item.assignmentAction?.actionType);
    }

    eventDateTime = String(item.assignmentAction?.dateTime);
    createdBy = item.initiator?.name || item.initiator?.id;
    adjusterName = item.assignedResource;
  } else if (isTaskEvent(item)) {
    eventCode = item.eventCode;
    eventDateTime = item.eventDateTime;
    createdBy = item.createdBy;
    adjusterName = item.adjusterName;
  }

  // Safely retrieve the icon component
  const IconComponent = eventCode
    ? timeLineLookup[eventCode]?.icon
    : QuestionMark;

  return (
    <TimelineItem>
      <TimelineOppositeContent style={{ paddingBottom: '50px' }}>
        <Typography variant="h6">
          {eventCode ? timeLineLookup[eventCode]?.title : 'Unknown Event'}
        </Typography>
        <Stack>
          {(eventCode === 'adjuster-assigned' ||
            eventCode === 'adjuster-reassigned') && (
            <>
              <Typography color="text.secondary">
                to {adjusterName || 'Unknown Adjuster'}
              </Typography>
              <Typography color="text.secondary">by {createdBy}</Typography>
            </>
          )}
          {eventCode && timeLineLookup[eventCode]?.showCreatedBy && (
            <Typography color="text.secondary">by {createdBy}</Typography>
          )}
        </Stack>
      </TimelineOppositeContent>
      <TimelineSeparator>
        <TimelineDot>
          <IconComponent />
        </TimelineDot>
        {!isLast && <TimelineConnector />}
      </TimelineSeparator>
      <TimelineContent color="text.secondary" style={{ paddingBottom: '50px' }}>
        {eventDateTime
          ? new Date(eventDateTime).toLocaleString()
          : 'Unknown Date'}
      </TimelineContent>
    </TimelineItem>
  );
};

const Render = ({
  data,
  status,
}: {
  data?: TaskAssignmentOrEvent[];
  status?: ResultStatus;
}) => {
  if (status === 'loading') {
    return <Loading />;
  }

  if (status === 'success' && data && data?.length > 0) {
    return (
      <Timeline>
        {data?.map((timeline, index) => (
          <RenderTimelineItem
            key={index}
            item={timeline}
            isLast={index === data.length - 1}
          />
        ))}
      </Timeline>
    );
  }

  return <Typography>No Data</Typography>;
};

export const TaskTimelineTab = ({ data, status }: TaskTimelineTabProps) => {
  return (
    <Section>
      <Render data={data} status={status} />
    </Section>
  );
};
